import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { purple, universalStyles } from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  image: {
    width: '100%',
    height: 'auto',
  },
  fullImage: {
    width: '80%',
    margin: '0 0 100px 10%',
  },
  listSection: {
    marginTop: 50,
  },
  columns: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 50,
  },
  columnImage: {
    width: '60%',
    height: 'auto',
    marginRight: 21,
  },
  note: {
    width: '100%',
    padding: '0 10px',
    boxSizing: 'border-box',
    border: `2px solid ${purple}`,
    marginBottom: 10,
    marginTop: 25,
  },
  noteText: {
    textAlign: 'center',
    color: purple,
  },
  subheader: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 25,
  },
  sectionHeader: {
    marginBottom: 25,
  },
  headerImage: {
    display: 'block',
    width: '100%',
  },
}));

type AcquisitionPage08Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage08 = ({ setNextDisabled }: AcquisitionPage08Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_4.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_4.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 4:</Typography>
          <Typography variant="h3" className={classes.sectionHeader}>
            Image Acquisition
          </Typography>
          <Typography variant="h5">How to Position Your Patient During Image Acquisition</Typography>
          <img
            className={classNames(classes.image, classes.fullImage)}
            src={`${BASE_URL}${IMAGE_PATHS.PROPER_POSITIONING.path}`}
            alt={`${IMAGE_PATHS.PROPER_POSITIONING.alt}`}
          />
          <Typography variant="h5">Image Acquisition Is Important for Biodistribution</Typography>
          <div className={classes.columns}>
            <img
              className={classNames(classes.image, classes.columnImage)}
              src={`${BASE_URL}${IMAGE_PATHS.BIODISTRIBUTION_GRAPH.path}`}
              alt={`${IMAGE_PATHS.BIODISTRIBUTION_GRAPH.alt}`}
            />
            <div>
              <div className={classes.note}>
                <Typography variant="body1" className={classes.noteText}>
                  <b>Note: </b>
                  Grey box denotes typical scan duration; mean and interpolated data from 6 subjects; study GE148-001
                </Typography>
              </div>
              <Typography className={classes.subheader}>Early Start:</Typography>
              <ul className={classes.list}>
                <li>
                  If scanning started early, biodistribution may be altered (eg, increased blood pool), and this should
                  be taken into account during image interpretation
                </li>
              </ul>
              <Typography className={classes.subheader}>Late Start:</Typography>
              <ul className={classes.list}>
                <li>
                  If scanning started late, biodistribution may be altered (eg, increased muscle uptake), and this
                  should be taken into account during image interpretation
                </li>
              </ul>
            </div>
          </div>
          <Typography variant="h5">Dosage, Administration, & Image Acquisition Timeline</Typography>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.DOSING_TIMELINE.path}`}
            alt={`${IMAGE_PATHS.DOSING_TIMELINE.alt}`}
          />
          <div className={classes.listSection}>
            <ul className={classes.list}>
              <li>Recommended that image acquisition should start at mid-thighs, just below the pelvis</li>
              <ul className={classes.nestedList}>
                <li>
                  <span>
                    It is
                    <strong> very important </strong>
                    that prostate bed is centrally positioned within the first bed position and that the inguinal lymph
                    nodes are included
                  </span>
                </li>
                <li>Coverage of imaging should extend to base of skull</li>
              </ul>
            </ul>
          </div>
        </div>
      </div>
      <LearningCheckQuestions module="axuminAcquisition" page={8} setNextDisabled={setNextDisabled} trainingId={1} />
    </>
  );
};

export default AcquisitionPage08;
